import { Observable, fromEvent, merge } from 'rxjs';

import { useSubscriptions } from '@/hook';
import {
  getAncestorList,
  getScrollableAncestorList,
  setStyleProperties,
} from '@/util';

import { VVirtualScroll } from '../VVirtualScroll';
import { new__cn as cn } from '../util';
import VBaseSelectItem from './VBaseSelectItem';
import { ITEM_HEIGHT } from './constant';
import useVBaseSelectContext from './hook';
import style from './style';
import type { VBaseSelectItem as _VBaseSelectItem } from './type';

const VBaseSelectItemList = () => {
  const {
    eventEmitter,
    itemList,
    itemListClassName,
    itemListHeight,
    itemListVisible,
    isSelected,
    renderItem,
    select,
    $displayValue,
    $itemList,
    fitContent,
  } = useVBaseSelectContext();
  const onItemClick = (item: _VBaseSelectItem) => {
    eventEmitter.emit('itemClick', { item });
    select(item.value);
  };

  const resizeItemList = () => {
    if (!$itemList.current || !$displayValue.current) return;

    const newHeight = `${itemList.length * ITEM_HEIGHT}px`;
    const newMaxHeight = `${itemListHeight}px`;

    // Safari/Firefox에서 무한 루프 방지를 위해 값이 변경된 경우에만 스타일 적용
    if (
      $itemList.current.style.getPropertyValue('height') !== newHeight ||
      $itemList.current.style.getPropertyValue('max-height') !== newMaxHeight
    ) {
      setStyleProperties($itemList.current, {
        height: newHeight,
        'max-height': newMaxHeight,
      });
    }

    let newWidth: string;

    if (fitContent) {
      const dummyDiv = document.createElement('div');

      dummyDiv.className = cn('absolute px-2 py-1 text-body-m opacity-0');
      document.body.append(dummyDiv);

      let maxWidth = 0;

      itemList.forEach(item => {
        dummyDiv.textContent = String(item.label);
        const itemWidth = dummyDiv.offsetWidth + 40; // check icon padding value
        maxWidth = Math.max(maxWidth, itemWidth);
      });

      const displayWidth = $displayValue.current.offsetWidth;
      newWidth = `${Math.min(Math.max(maxWidth, displayWidth), displayWidth * 1.5)}px`;

      dummyDiv.remove();
    } else {
      newWidth = `${$displayValue.current.offsetWidth}px`;
    }

    if ($itemList.current.style.getPropertyValue('width') !== newWidth) {
      setStyleProperties($itemList.current, { width: newWidth });
    }
  };

  useSubscriptions(() => {
    if (!itemListVisible) return [];

    const displayValue = $displayValue.current;
    const itemList = $itemList.current;

    if (!displayValue || !itemList) return [];

    resizeItemList();

    const scrollableAncestorList = getScrollableAncestorList(displayValue);
    const resize$ = fromEvent(window, 'resize');
    const scroll$ = merge(
      ...scrollableAncestorList.map(el =>
        fromEvent(el, 'scroll', { passive: true }),
      ),
    );

    const ancestorResize$ = new Observable(subscriber => {
      const resizeObserver = new ResizeObserver(() => subscriber.next(null));

      resizeObserver.observe(displayValue);
      getAncestorList(displayValue).forEach(el => resizeObserver.observe(el));

      return () => resizeObserver.disconnect();
    });

    const merged$ = merge(resize$, scroll$, ancestorResize$);

    return [merged$.subscribe(resizeItemList)];
  }, [itemListVisible, itemList, fitContent]);

  return (
    <div
      ref={$itemList}
      data-testid="v-base-select-item-list"
      className={cn(style.vBaseSelectItemList, itemListClassName)}
    >
      <VVirtualScroll
        className="w-full divide-y divide-gray-200"
        item={{
          list: itemList,
          gap: 0,
          height: ITEM_HEIGHT,
        }}
        renderItem={item => (
          <VBaseSelectItem
            {...item}
            selected={isSelected(item.value)}
            renderItem={renderItem}
            onClick={onItemClick}
          />
        )}
      />
    </div>
  );
};

export default VBaseSelectItemList;
